import React, { useState, useEffect } from 'react';

function Providers() {
  const [providers, setProviders] = useState([]);
  const [sipStatus, setSipStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentProvider, setCurrentProvider] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    sip_server: '',
    username: '',
    tech_prefix: '',
    password: '',
    cost_per_minute: 0,
    connection_charge: 0,
    status: 'active'
  });

  useEffect(() => {
    fetchProviders();
    fetchSipStatus();
    
    // Poll SIP status every 10 seconds
    const interval = setInterval(fetchSipStatus, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchProviders = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/providers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setProviders(data);
    } catch (error) {
      console.error('Error fetching providers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSipStatus = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sip/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setSipStatus(data.sip_status || {});
    } catch (error) {
      console.error('Error fetching SIP status:', error);
    }
  };

  const getConnectionStatus = (provider) => {
    // Check if any customer using this provider is registered
    const isConnected = Object.keys(sipStatus).some(sip => {
      return sipStatus[sip] === 'online';
    });
    
    if (provider.status === 'active' && isConnected) {
      return <span style={{ color: '#22c55e', fontSize: '1.2em' }} title="Connected & Active">●</span>;
    } else if (provider.status === 'active') {
      return <span style={{ color: '#f59e0b', fontSize: '1.2em' }} title="Active but no registrations">●</span>;
    }
    return <span style={{ color: '#ef4444', fontSize: '1.2em' }} title="Inactive">●</span>;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    
    try {
      if (editMode) {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/providers/${currentProvider.provider_id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });
        
        if (response.ok) {
          alert('Provider updated successfully');
          fetchProviders();
          setShowModal(false);
        }
      } else {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/providers`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });
        
        if (response.ok) {
          alert('Provider created successfully');
          fetchProviders();
          setShowModal(false);
        }
      }
    } catch (error) {
      console.error('Error saving provider:', error);
      alert('Error saving provider');
    }
  };

  const handleEdit = (provider) => {
    setCurrentProvider(provider);
    setFormData({
      name: provider.name,
      sip_server: provider.sip_server,
      username: provider.username,
      tech_prefix: provider.tech_prefix || '',
      password: provider.password || '',
      cost_per_minute: provider.cost_per_minute || 0,
      connection_charge: provider.connection_charge || 0,
      status: provider.status
    });
    setEditMode(true);
    setShowModal(true);
  };

  const handleDelete = async (providerId) => {
    if (!window.confirm('Are you sure you want to delete this provider?')) {
      return;
    }

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/providers/${providerId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        alert('Provider deleted successfully');
        fetchProviders();
      }
    } catch (error) {
      console.error('Error deleting provider:', error);
      alert('Error deleting provider');
    }
  };

  const openAddModal = () => {
    setEditMode(false);
    setCurrentProvider(null);
    setFormData({
      name: '',
      sip_server: '',
      username: '',
      tech_prefix: '',
      password: '',
      cost_per_minute: 0,
      connection_charge: 0,
      status: 'active'
    });
    setShowModal(true);
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="table-container">
      <div className="table-header">
        <h2>SIP Providers</h2>
        <button className="btn-primary" onClick={openAddModal}>Add Provider</button>
      </div>
      
      {/* FIXED: Responsive wrapper */}
      <div style={{ overflowX: 'auto', width: '100%' }}>
        <table style={{ minWidth: '1000px' }}>
          <thead>
            <tr>
              <th>CONN STATUS</th>
              <th>Name</th>
              <th>SIP Server</th>
              <th>Username</th>
              <th>Tech Prefix</th>
              <th>Cost/Min ($)</th>
              <th>Connection Fee ($)</th>
              <th>Status</th>
              <th style={{ position: 'sticky', right: 0, background: 'white', boxShadow: '-2px 0 5px rgba(0,0,0,0.1)' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {providers.map(provider => (
              <tr key={provider.provider_id}>
                <td style={{ textAlign: 'center' }}>
                  {getConnectionStatus(provider)}
                </td>
                <td>{provider.name}</td>
                <td><code className="code-text">{provider.sip_server}</code></td>
                <td>{provider.username}</td>
                <td><code className="code-text">{provider.tech_prefix}</code></td>
                <td>${provider.cost_per_minute?.toFixed(4)}</td>
                <td>${(provider.connection_charge || 0).toFixed(2)}</td>
                <td>
                  <span className={`status-badge status-${provider.status}`}>
                    {provider.status}
                  </span>
                </td>
                <td style={{ position: 'sticky', right: 0, background: 'white', whiteSpace: 'nowrap' }}>
                  <button className="btn-edit" onClick={() => handleEdit(provider)}>Edit</button>
                  <button className="btn-delete" onClick={() => handleDelete(provider.provider_id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h2>{editMode ? 'Edit Provider' : 'Add New Provider'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name:</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>SIP Server:</label>
                <input
                  type="text"
                  value={formData.sip_server}
                  onChange={(e) => setFormData({...formData, sip_server: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Username:</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({...formData, username: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Password:</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required={!editMode}
                />
              </div>
              <div className="form-group">
                <label>Tech Prefix:</label>
                <input
                  type="text"
                  value={formData.tech_prefix}
                  onChange={(e) => setFormData({...formData, tech_prefix: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Cost per Minute ($):</label>
                <input
                  type="number"
                  step="0.0001"
                  value={formData.cost_per_minute}
                  onChange={(e) => setFormData({...formData, cost_per_minute: parseFloat(e.target.value) || 0})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Connection Fee ($):</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.connection_charge}
                  onChange={(e) => setFormData({...formData, connection_charge: parseFloat(e.target.value) || 0})}
                  required
                />
                <small style={{color: "#666", fontSize: "12px"}}>Charged per call attempt</small>
              </div>
              <div className="form-group">
                <label>Status:</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="modal-actions" style={{ position: 'sticky', bottom: 0, background: 'white', padding: '1rem 0', borderTop: '1px solid #e5e7eb' }}>
                <button type="submit" className="btn-primary">{editMode ? 'Update' : 'Create'}</button>
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Providers;
